/**
 * Shared money-rounding helpers for the POS.
 *
 * Policy: ROUND_HALF_UP to 2 decimals (ties away from zero), never `-0`.
 * This MUST match the server implementation byte-for-byte
 * (`niceloop-pos-server-v1/src/utils/money.ts`) — the whole point is that the
 * printed receipt and the server report agree to the satang.
 *
 * Every money value the POS *stores* must pass through this. Storing full
 * floating precision (10+ decimals) forces the server to repair the receipt at
 * read time, and the repaired value can differ from what was printed on the
 * customer's slip by a satang. See `HANDOFF-money-rounding-frontend.md`.
 */

const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value.trim())
    if (Number.isFinite(parsed)) return parsed
  }
  return NaN
}

/** Round a monetary value to 2 decimals (ROUND_HALF_UP). Non-numeric -> 0. */
export const roundMoney = (value: unknown): number => {
  const n = toNumber(value)
  if (!Number.isFinite(n)) return 0
  const rounded = Math.round((Math.abs(n) + Number.EPSILON) * 100) / 100
  // `rounded === 0` guard keeps a tiny negative (-0.001) from becoming `-0`,
  // which would print as "-0.00" on a slip. Numerically identical to the server.
  return n < 0 && rounded !== 0 ? -rounded : rounded
}

/** True when `n` carries no more than 2 decimals. */
export const is2dp = (n: number): boolean =>
  Number.isFinite(n) && Math.abs(n * 100 - Math.round(n * 100)) < 1e-6

export interface BillInvariantFields {
  SubTotal: number
  ItemDiscount: number
  Discount: number
  ServiceCharge: number
  CardCharge: number
  Vat: number
  VatMode: number | string
  Rounding: number
  GrandTotal: number
}

/**
 * The single invariant every stored receipt must satisfy, at exactly 2 decimals:
 *
 *   SubTotal - |ItemDiscount| + ServiceCharge - |Discount| + CardCharge
 *     + VAT_in_total + Rounding === GrandTotal
 *
 * where VAT_in_total = (VatMode === 2) ? Vat : 0 — inclusive VAT (mode 1) is
 * already inside SubTotal and is informational only, so adding it would force
 * `Rounding` to absorb roughly -VAT and blow up to thousands.
 *
 * Returns null when the bill is well-formed, otherwise a human-readable reason.
 * Callers decide whether to throw (dev/QA) or just log (production) — a
 * malformed bill must never block a real customer's payment.
 */
export const checkBillInvariant = (b: BillInvariantFields): string | null => {
  for (const [key, value] of Object.entries(b)) {
    if (key === 'VatMode') continue
    if (typeof value === 'number' && !is2dp(value)) {
      return `${key}=${value} is not 2 decimals`
    }
  }

  const vatInTotal = Number(b.VatMode) === 2 ? b.Vat : 0
  const lhs = roundMoney(
    b.SubTotal -
    Math.abs(b.ItemDiscount) +
    b.ServiceCharge -
    Math.abs(b.Discount) +
    b.CardCharge +
    vatInTotal +
    b.Rounding
  )

  if (roundMoney(lhs - b.GrandTotal) !== 0) {
    return `Invariant broken: ${lhs} !== GrandTotal ${b.GrandTotal}`
  }
  return null
}

/** Throwing form of {@link checkBillInvariant}, for tests and dev builds. */
export const assertBillInvariant = (b: BillInvariantFields): void => {
  const reason = checkBillInvariant(b)
  if (reason) throw new Error(reason)
}
